// src/services/api.js
import axios from 'axios';
import useAuthStore from '../store/authStore';

const api = axios.create({
  baseURL: process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:5025/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ FIXED: Response interceptor - Development only logs, Production silent
api.interceptors.response.use(
  (response) => {
    // ✅ Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API [${response.config.method?.toUpperCase()}] ${response.config.url} - Status: ${response.status}`);
    }
    return response;
  },
  (error) => {
    // Handle 401 errors
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/admin/login';
    }
    
    // ✅ Only log errors in development mode
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
export const adminAPI = {
  // Dashboard stats
  getStats: () => api.get('/admin/stats'),
  
  // Users management
  getUsers: (params) => api.get('/users', { params }),
  getUserById: (userId) => api.get(`/users/${userId}`),
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  createUser: (userData) => api.post('/users', userData),
  
  // Roles management
  getRoles: () => api.get('/roles'),
  getRoleById: (roleId) => api.get(`/roles/${roleId}`),
  createRole: (roleData) => api.post('/roles', roleData),
  updateRole: (roleId, roleData) => api.put(`/roles/${roleId}`, roleData),
  deleteRole: (roleId) => api.delete(`/roles/${roleId}`),
  
  // Assign role
  assignRoleToUser: (userId, roleId) => api.post('/assign-role', { userId, roleId }),
  
  // Contacts management
  getContacts: (params) => api.get('/contacts', { params }),
  getContactById: (contactId) => api.get(`/contacts/${contactId}`),
  updateContactStatus: (contactId, status) => api.patch(`/contacts/${contactId}/status`, { status }),
  deleteContact: (contactId) => api.delete(`/contacts/${contactId}`),
  createContact: (contactData) => api.post('/contacts', contactData),
  
  // Inquiries
  getInquiries: (params) => api.get('/contacts', { ...params, source: 'inquiry' }),
  updateInquiryStatus: (inquiryId, status) => api.patch(`/contacts/${inquiryId}/status`, { status }),
  deleteInquiry: (inquiryId) => api.delete(`/contacts/${inquiryId}`),
};

export default api;